import React, { useState } from 'react';
import { useParticipant, ParticipantProfile } from '../../contexts/ParticipantContext';
import { BrButton, BrSelect, BrCheckbox, BrRadio, BrMessage } from '@govbr-ds/react-components';

interface OnboardingFormProps {
    onComplete: () => void;
}

export default function OnboardingForm({ onComplete }: OnboardingFormProps) {
    const { startSession, state } = useParticipant();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState<Partial<ParticipantProfile>>({
        age_range: '',
        occupation: '',
        education: '',
        internet_frequency: '',
        judicial_experience: '',
        device_preference: '',
        accessibility_needs: [],
        language_preference: undefined,
        tcle_accepted: false
    });

    const handleChange = (field: keyof ParticipantProfile, value: string | boolean | string[]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAccessibilityChange = (need: string, checked: boolean) => {
        const current = formData.accessibility_needs || [];
        if (checked) {
            if (need === 'Nenhum') {
                handleChange('accessibility_needs', ['Nenhum']);
            } else {
                handleChange('accessibility_needs', [...current.filter(n => n !== 'Nenhum'), need]);
            }
        } else {
            handleChange('accessibility_needs', current.filter(n => n !== need));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.tcle_accepted) {
            alert('Você precisa aceitar o termo de consentimento para continuar.');
            return;
        }

        await startSession(formData as ParticipantProfile);
        onComplete();
    };

    const isStep1Valid = formData.age_range && formData.occupation && formData.education;
    const isStep2Valid = formData.internet_frequency && formData.judicial_experience && formData.device_preference;
    const isStep3Valid = formData.accessibility_needs && formData.accessibility_needs.length > 0 && formData.language_preference;

    const ageOptions = [
        { value: '', label: 'Selecione...' },
        { value: '18-29', label: '18 a 29 anos' },
        { value: '30-44', label: '30 a 44 anos' },
        { value: '45-59', label: '45 a 59 anos' },
        { value: '60+', label: '60 anos ou mais' }
    ];

    const occupationOptions = [
        { value: '', label: 'Selecione...' },
        { value: 'Empregado CLT', label: 'Empregado CLT' },
        { value: 'Autônomo', label: 'Autônomo' },
        { value: 'Aposentado', label: 'Aposentado' },
        { value: 'Desempregado', label: 'Desempregado' },
        { value: 'Advogado', label: 'Advogado(a)' },
        { value: 'Outro', label: 'Outro' }
    ];

    const educationOptions = [
        { value: '', label: 'Selecione...' },
        { value: 'Fundamental', label: 'Ensino Fundamental' },
        { value: 'Médio', label: 'Ensino Médio' },
        { value: 'Superior', label: 'Ensino Superior' },
        { value: 'Pós-graduação', label: 'Pós-graduação' }
    ];

    const internetOptions = [
        { value: '', label: 'Selecione...' },
        { value: 'Diariamente', label: 'Diariamente' },
        { value: 'Algumas vezes por semana', label: 'Algumas vezes por semana' },
        { value: 'Raramente', label: 'Raramente' },
        { value: 'Quase nunca', label: 'Quase nunca' }
    ];

    const judicialOptions = [
        { value: '', label: 'Selecione...' },
        { value: 'Nunca acessei', label: 'Nunca acessei' },
        { value: '1-2 vezes', label: '1 a 2 vezes' },
        { value: '3-5 vezes', label: '3 a 5 vezes' },
        { value: 'Mais de 5 vezes', label: 'Mais de 5 vezes' }
    ];

    const deviceOptions = [
        { value: '', label: 'Selecione...' },
        { value: 'Celular', label: 'Celular' },
        { value: 'Computador', label: 'Computador' },
        { value: 'Ambos', label: 'Ambos igualmente' }
    ];

    return (
        <form onSubmit={handleSubmit} className="d-flex flex-column gap-4">
            {/* Step 1: Demographics */}
            {step === 1 && (
                <div className="d-flex flex-column gap-4">
                    <BrMessage status="info" title="Passo 1 de 3: Sobre Você" message="Essas informações nos ajudam a personalizar sua experiência." />

                    <div className="d-flex flex-column gap-3">
                        <BrSelect
                            id="age-range"
                            label="Qual sua faixa etária? *"
                            options={ageOptions}
                            value={formData.age_range}
                            onChange={(value) => handleChange('age_range', value as string)}
                        />

                        <BrSelect
                            id="occupation"
                            label="Qual sua ocupação principal? *"
                            options={occupationOptions}
                            value={formData.occupation}
                            onChange={(value) => handleChange('occupation', value as string)}
                        />

                        <BrSelect
                            id="education"
                            label="Qual seu nível de escolaridade? *"
                            options={educationOptions}
                            value={formData.education}
                            onChange={(value) => handleChange('education', value as string)}
                        />
                    </div>

                    <BrButton
                        primary
                        block
                        onClick={() => setStep(2)}
                        disabled={!isStep1Valid}
                    >
                        Continuar
                    </BrButton>
                </div>
            )}

            {/* Step 2: Experience */}
            {step === 2 && (
                <div className="d-flex flex-column gap-4">
                    <BrMessage status="info" title="Passo 2 de 3: Sua Experiência" message="Queremos entender melhor seu uso de tecnologia e experiência com processos judiciais." />

                    <div className="d-flex flex-column gap-3">
                        <BrSelect
                            id="internet-frequency"
                            label="Com que frequência você usa a internet? *"
                            options={internetOptions}
                            value={formData.internet_frequency}
                            onChange={(value) => handleChange('internet_frequency', value as string)}
                        />

                        <BrSelect
                            id="judicial-experience"
                            label="Você já acessou algum site de consulta processual? *"
                            options={judicialOptions}
                            value={formData.judicial_experience}
                            onChange={(value) => handleChange('judicial_experience', value as string)}
                        />

                        <BrSelect
                            id="device-preference"
                            label="Qual dispositivo você mais usa para acessar a internet? *"
                            options={deviceOptions}
                            value={formData.device_preference}
                            onChange={(value) => handleChange('device_preference', value as string)}
                        />
                    </div>

                    <div className="d-flex gap-3">
                        <BrButton
                            secondary
                            onClick={() => setStep(1)}
                            className="flex-fill"
                        >
                            Voltar
                        </BrButton>
                        <BrButton
                            primary
                            onClick={() => setStep(3)}
                            disabled={!isStep2Valid}
                            className="flex-fill"
                        >
                            Continuar
                        </BrButton>
                    </div>
                </div>
            )}

            {/* Step 3: Accessibility & TCLE */}
            {step === 3 && (
                <div className="d-flex flex-column gap-4">
                    <BrMessage status="info" title="Passo 3 de 3: Preferências e Consentimento" message="Suas preferências nos ajudam a adaptar a interface para você." />

                    <div className="d-flex flex-column gap-4">
                        <fieldset>
                            <legend className="text-weight-medium mb-2">
                                Você tem alguma necessidade especial? (marque todas que se aplicam) *
                            </legend>
                            <div className="d-flex flex-column gap-2">
                                {[
                                    'Baixa visão',
                                    'Fotofobia',
                                    'Nenhum'
                                ].map((need) => (
                                    <BrCheckbox
                                        key={need}
                                        id={`accessibility-${need}`}
                                        label={need}
                                        checked={formData.accessibility_needs?.includes(need) || false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAccessibilityChange(need, e.target.checked)}
                                    />
                                ))}
                            </div>
                        </fieldset>

                        <fieldset>
                            <legend className="text-weight-medium mb-2">
                                Que tipo de linguagem você prefere? *
                            </legend>
                            <div className="d-flex flex-column gap-2">
                                <BrRadio
                                    id="language-simple"
                                    name="language"
                                    label="Prefiro textos simples e diretos, sem termos técnicos"
                                    value="simple"
                                    checked={formData.language_preference === 'simple'}
                                    onChange={() => handleChange('language_preference', 'simple')}
                                />
                                <BrRadio
                                    id="language-technical"
                                    name="language"
                                    label="Prefiro textos técnicos e detalhados"
                                    value="technical"
                                    checked={formData.language_preference === 'technical'}
                                    onChange={() => handleChange('language_preference', 'technical')}
                                />
                            </div>
                        </fieldset>

                        {/* TCLE */}
                        <div className="br-card">
                            <div className="card-content">
                                <h3 className="text-weight-semi-bold mb-2">Termo de Consentimento Livre e Esclarecido</h3>
                                <div className="text-sm text-gray-60 mb-3" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                    <p>Você está sendo convidado(a) a participar de uma pesquisa sobre interfaces de consulta processual, conduzida pela Universidade de Brasília (UnB).</p>
                                    <p className="mt-2">Sua participação é voluntária e você pode desistir a qualquer momento. Os dados coletados serão anonimizados e utilizados apenas para fins acadêmicos.</p>
                                    <p className="mt-2">Tempo estimado: 10-15 minutos.</p>
                                </div>
                                <BrCheckbox
                                    id="tcle-accepted"
                                    label="Li e aceito o termo de consentimento"
                                    checked={formData.tcle_accepted || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('tcle_accepted', e.target.checked)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex gap-3">
                        <BrButton
                            secondary
                            onClick={() => setStep(2)}
                            className="flex-fill"
                        >
                            Voltar
                        </BrButton>
                        <BrButton
                            primary
                            type="submit"
                            disabled={!isStep3Valid || !formData.tcle_accepted || state.isLoading}
                            className="flex-fill"
                        >
                            {state.isLoading ? 'Carregando...' : 'Iniciar Pesquisa'}
                        </BrButton>
                    </div>

                    {state.error && (
                        <BrMessage status="danger" message={state.error} />
                    )}
                </div>
            )}
        </form>
    );
}
